import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { StatTile } from '@/components/StatTile';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AccountDayBars } from '@/components/whatsapp/AccountDayBars';
import {
  whatsappAccountService, WhatsappAccount, AccountStats, AccountStatsMap,
  AccountActivity, AccountActivityMap,
} from '@/services/whatsapp-account.service';
import { useActiveAccount } from '@/hooks/useActiveAccount';
import {
  Phone, Plus, Settings, Trash2, Power, RefreshCw, MessageSquare,
  ArrowDownLeft, ArrowUpRight, LayoutTemplate, BellOff, Image as ImageIcon, FileText, Webhook,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ConnectAccountModal } from './ConnectAccountModal';
import { CardGridSkeleton } from '@/components/PageSkeletons';

type Props = {
  /** Rendered inside WhatsappAccountsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
  /** When embedded, the gear switches tab instead of navigating away. */
  onOpenProfile?: (accountUid: string) => void;
};

export default function WhatsappAccountsList({ embedded = false, onOpenProfile }: Props = {}) {
  const [accounts, setAccounts] = useState<WhatsappAccount[] | null>(null);
  const [stats, setStats] = useState<AccountStatsMap>({});
  const [activity, setActivity] = useState<AccountActivityMap>({});
  const [connectOpen, setConnectOpen] = useState(false);
  const [reconnect, setReconnect] = useState<WhatsappAccount | null>(null);
  const [del, setDel] = useState<WhatsappAccount | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const { refresh: refreshGlobal } = useActiveAccount();
  const nav = useNavigate();

  const refresh = async () => {
    try {
      const [list, statsMap, act] = await Promise.all([
        whatsappAccountService.list(),
        whatsappAccountService.stats().catch(() => ({})),
        whatsappAccountService.activity(15).catch(() => ({} as AccountActivityMap)),
      ]);
      setAccounts(list);
      setStats(statsMap);
      setActivity(act);
    } catch { setAccounts([]); }
    await refreshGlobal();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  const onManualRefresh = async () => {
    setRefreshing(true);
    try { await refresh(); } finally { setRefreshing(false); }
  };

  // One number is live at a time — switching to an account turns every other
  // account off (enforced server-side too).
  const onUseAccount = async (acc: WhatsappAccount) => {
    if (acc.isDefault && acc.isActive) return;
    setBusy(acc.accountUid);
    try {
      await whatsappAccountService.setDefault(acc.accountUid);
      toast.success(`Now using ${acc.businessName} (${acc.displayPhoneNumber}) — other numbers are switched off`);
      await refresh();
    } catch {
      // axios interceptor already toasted backend message
    } finally { setBusy(null); }
  };

  const onDelete = async () => {
    if (!del) return;
    setBusy(del.accountUid);
    try {
      await whatsappAccountService.remove(del.accountUid);
      toast.success(`${del.businessName} removed`);
      setDel(null);
      await refresh();
    } catch {
      // axios interceptor already toasted backend message
    } finally { setBusy(null); }
  };

  const onConnected = () => { setConnectOpen(false); setReconnect(null); refresh(); };

  // Company-wide roll-up across every connected number.
  const totals = useMemo(() => {
    const t = { sent: 0, delivered: 0, read: 0, failed: 0, received: 0, standard: 0, carousel: 0 };
    Object.values(stats).forEach((s) => {
      t.sent += s.sent; t.delivered += s.delivered; t.read += s.read; t.failed += s.failed;
    });
    Object.values(activity).forEach((a) => {
      t.received += a.received;
      t.standard += a.templates.standard;
      t.carousel += a.templates.carousel;
    });
    return t;
  }, [stats, activity]);

  const actions = (
    <div className="flex items-center gap-2">
      <Button variant="secondary" onClick={onManualRefresh} loading={refreshing} iconLeft={<RefreshCw size={14} />}>
        Refresh
      </Button>
      <Button variant="primary" onClick={() => setConnectOpen(true)}>
        <Plus size={16} /> Connect Account
      </Button>
    </div>
  );

  return (
    <>
      {embedded ? (
        <div className="flex items-center justify-end gap-2 px-7 pt-4">{actions}</div>
      ) : (
        <Topbar
          title="WhatsApp Accounts"
          breadcrumb="WhatsApp"
          description={accounts ? `Manage and switch between your WhatsApp Business accounts · ${accounts.length} account${accounts.length === 1 ? '' : 's'}` : 'Manage and switch between your WhatsApp Business accounts.'}
          right={actions}
        />
      )}

      <div className={clsx('w-full space-y-5 px-7 pb-24', embedded ? 'pt-4' : 'pt-6')}>
        {accounts === null && (
          <CardGridSkeleton count={3} className="gap-4 sm:grid-cols-1 xl:grid-cols-3" />
        )}

        {accounts && accounts.length === 0 && (
          <EmptyState
            title="No WhatsApp accounts yet"
            description="Connect your first WhatsApp Business account to start sending messages and templates."
            action={<Button onClick={() => setConnectOpen(true)}><Plus size={16} /> Connect Account</Button>}
          />
        )}

        {accounts && accounts.length > 0 && (
          <>
            {/* Company-wide summary */}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <Card padded>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">To customers</div>
                    <div className="mt-2 font-display text-[28px] font-bold tabular-nums text-text-primary">
                      {totals.sent.toLocaleString()}
                    </div>
                    <div className="mt-0.5 text-xs text-text-muted">All time, across every number</div>
                  </div>
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-success-soft text-success">
                    <ArrowUpRight size={16} />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <MiniStat label="Delivered" value={totals.delivered} tone="text-success" />
                  <MiniStat label="Read" value={totals.read} tone="text-info" />
                  <MiniStat label="Failed" value={totals.failed} tone="text-danger" />
                </div>
              </Card>

              <StatTile
                label="From customers"
                value={totals.received.toLocaleString()}
                delta="Inbound messages received"
                trend="neutral"
                accent="blue"
                icon={<ArrowDownLeft size={16} />}
              />

              <Card padded>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">Templates</div>
                    <div className="mt-2 font-display text-[28px] font-bold tabular-nums text-text-primary">
                      {(totals.standard + totals.carousel).toLocaleString()}
                    </div>
                    <div className="mt-0.5 text-xs text-text-muted">Approved and draft, all accounts</div>
                  </div>
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-soft text-primary">
                    <LayoutTemplate size={16} />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <MiniStat label="Standard" value={totals.standard} icon={<FileText size={11} />} />
                  <MiniStat label="Carousel" value={totals.carousel} icon={<ImageIcon size={11} />} />
                </div>
              </Card>
            </div>

            {/* One card per number */}
            <div className="grid gap-4 xl:grid-cols-2 2xl:grid-cols-3">
              {accounts.map((acc) => (
                <AccountCard
                  key={acc.accountUid}
                  acc={acc}
                  stats={stats[acc.accountUid]}
                  activity={activity[acc.accountUid]}
                  busy={busy === acc.accountUid}
                  onUse={() => onUseAccount(acc)}
                  onDelete={() => setDel(acc)}
                  onReconnect={() => { setReconnect(acc); setConnectOpen(true); }}
                  onRefresh={refresh}
                  onOpenSettings={() =>
                    onOpenProfile
                      ? onOpenProfile(acc.accountUid)
                      : nav(`/whatsapp-accounts?tab=profile&account=${acc.accountUid}`)
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>

      <ConnectAccountModal reconnectAccount={reconnect} open={connectOpen} onClose={() => { setConnectOpen(false); setReconnect(null); }} onConnected={onConnected} />

      <ConfirmDialog
        open={!!del}
        title="Disconnect WhatsApp account?"
        message={del ? `${del.businessName} (${del.displayPhoneNumber}) will be disconnected from this CRM and its saved authorization removed. Existing chats and history stay here. A send already in progress may finish. This does not delete or deregister your number at Meta.` : ''}
        danger
        onConfirm={onDelete}
        onCancel={() => setDel(null)}
      />
    </>
  );
}

function MiniStat({ label, value, tone, icon }: { label: string; value: number; tone?: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface-alt/40 px-2 py-1.5 text-center">
      <div className={clsx('font-display text-base font-bold tabular-nums', tone ?? 'text-text-primary')}>
        {value.toLocaleString()}
      </div>
      <div className="mt-0.5 flex items-center justify-center gap-1 text-[10px] uppercase tracking-wide text-text-muted">
        {icon}{label}
      </div>
    </div>
  );
}

const QUALITY: Record<string, { cls: string; label: string }> = {
  GREEN: { cls: 'bg-success-soft text-success', label: 'High quality' },
  YELLOW: { cls: 'bg-warning-soft text-warning', label: 'Medium quality' },
  RED: { cls: 'bg-danger-soft text-danger', label: 'Low quality' },
};

interface AccountCardProps {
  acc: WhatsappAccount;
  stats?: AccountStats;
  activity?: AccountActivity;
  busy: boolean;
  onUse: () => void;
  onDelete: () => void;
  onReconnect: () => void;
  onRefresh: () => Promise<void>;
  onOpenSettings: () => void;
}

function AccountCard({ acc, stats, activity, busy, onUse, onDelete, onOpenSettings, onReconnect, onRefresh }: AccountCardProps) {
  const s = stats ?? { sent: 0, delivered: 0, read: 0, failed: 0 };
  const q = acc.qualityRating ? QUALITY[acc.qualityRating] : undefined;
  const last = activity?.last;
  // Single-active model: the default account IS the live one.
  const inUse = acc.isDefault && acc.isActive;
  const [fixingWebhook, setFixingWebhook] = useState(false);

  // Meta only sends webhooks (inbound messages, delivery receipts) for a WABA
  // that has our app subscribed — this checks and repairs that subscription.
  const fixWebhook = async () => {
    setFixingWebhook(true);
    try {
      const r = await whatsappAccountService.subscribeWebhook(acc.accountUid);
      if (r.error) {
        toast.error(`Webhook subscription failed: ${r.error}`);
      } else if (r.subscribedNow) {
        toast.success(
          r.subscribedBefore
            ? 'Webhook subscription is active ✓'
            : 'Webhook subscription repaired ✓ — inbound messages and delivery receipts should now arrive.',
          { duration: 8000 },
        );
      } else {
        toast.error('Still not subscribed — check the webhook URL and fields in the Meta App Dashboard.');
      }
    } catch { /* interceptor toasts */ }
    finally { setFixingWebhook(false); await onRefresh(); }
  };

  return (
    <Card
      bordered
      padded={false}
      className={clsx(
        'flex flex-col overflow-hidden transition-shadow',
        inUse && 'ring-2 ring-primary/40',
      )}
    >
      {/* State strip — the first thing the eye lands on. Green = the one
          number the CRM sends and receives on; grey = standby. */}
      {inUse ? (
        <div className="flex items-center gap-2 bg-success-soft px-5 py-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
          </span>
          <span className="text-2xs font-bold uppercase tracking-[0.14em] text-success">In use</span>
          <span className="text-2xs text-text-muted">— selected for new outgoing messages</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 bg-surface-alt px-5 py-2">
          <BellOff size={11} className="text-text-muted" />
          <span className="text-2xs font-bold uppercase tracking-[0.14em] text-text-muted">Not in use</span>
          <span className="text-2xs text-text-muted">— select this number before replying to its chats</span>
        </div>
      )}

      {/* Header */}
      <div className={clsx('flex items-start gap-3 px-5 pb-3 pt-4', !inUse && 'opacity-60')}>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-soft text-primary">
          {acc.profilePicUrl
            ? <img src={acc.profilePicUrl} alt="" className="h-11 w-11 object-cover" />
            : <Phone size={20} />}
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="truncate font-display text-[15px] font-semibold text-text-primary">{acc.businessName}</h4>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-text-muted">
            <span className="inline-flex items-center gap-1"><Phone size={11} /> {acc.displayPhoneNumber}</span>
            {q && (
              <span className={clsx('inline-block rounded-full px-1.5 py-0.5 text-2xs font-medium', q.cls)}>{q.label}</span>
            )}
          </div>
        </div>
        <button
          onClick={onOpenSettings}
          disabled={busy}
          className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
          title="Account settings"
        >
          <Settings size={15} />
        </button>
      </div>

      {/* Standby note — the figures below are history, not live traffic. */}
      {!inUse && (
        <div className="mx-5 mb-3 flex items-start gap-2 rounded-lg border border-border bg-surface-alt/60 px-3 py-2">
          <BellOff size={12} className="mt-0.5 shrink-0 text-text-muted" />
          <p className="text-2xs leading-snug text-text-secondary">
            Incoming messages keep their original number. Press <strong>Use this number</strong> to reply here. Changing the selected number never transfers a chat to a different sender.
          </p>
        </div>
      )}

      <div className="mx-5 mb-3 space-y-1 rounded-lg border border-border bg-surface-alt/40 p-3 text-2xs text-text-secondary">
        <div className="flex justify-between gap-2"><span>Webhook subscription</span><span className={acc.webhookSubscriptionError ? 'text-warn' : 'text-text-primary'}>{acc.webhookSubscriptionStatus === 'subscribed' ? 'Verified' : acc.webhookSubscriptionStatus === 'failed' ? 'Needs attention' : 'Check required'}</span></div>
        <div className="flex justify-between gap-2"><span>Phone registration</span><span>{acc.registrationStatus === 'registered' ? 'Confirmed' : acc.registrationStatus === 'failed' ? 'Needs attention' : 'Not checked in CRM'}</span></div>
        {[acc.webhookSubscriptionError, acc.registrationError, acc.healthError].filter(Boolean).map((error, i) => <p key={i} className="pt-1 text-warn">{error}</p>)}
      </div>
      {/* Direction split */}
      <div className="grid grid-cols-[auto_1fr] gap-3 border-t border-border bg-surface-alt/40 px-5 py-3">
        <div className="pr-3">
          <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
            <ArrowDownLeft size={10} /> From customers
          </div>
          <div className="mt-1 font-display text-xl font-bold tabular-nums text-info">
            {(activity?.received ?? 0).toLocaleString()}
            <span className="ml-1 text-2xs font-normal text-text-muted">received</span>
          </div>
        </div>
        <div className="border-l border-border pl-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              <ArrowUpRight size={10} /> To customers
            </div>
            <span className="text-2xs text-text-muted">{s.sent.toLocaleString()} sent</span>
          </div>
          <div className="mt-1 grid grid-cols-3 gap-2">
            <SmallNum value={s.delivered} label="Delivered" tone="text-success" />
            <SmallNum value={s.read} label="Read" tone="text-info" />
            <SmallNum value={s.failed} label="Failed" tone="text-danger" />
          </div>
        </div>
      </div>

      {/* Daily volume */}
      <div className="px-5 py-3">
        <AccountDayBars days={activity?.daily ?? []} />
      </div>

      {/* Last message */}
      {last && (
        <div className="mx-5 mb-3 rounded-lg border border-border bg-surface-alt/30 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              {last.direction === 'in'
                ? <><ArrowDownLeft size={10} /> From customer</>
                : <><ArrowUpRight size={10} /> To customer</>}
            </span>
            <span className="text-2xs text-text-muted">
              {new Date(last.at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
          </div>
          <p className="mt-1 line-clamp-2 text-xs text-text-secondary">
            {last.text ?? <span className="italic text-text-muted">Non-text message</span>}
          </p>
        </div>
      )}

      {/* Footer — one action: make this the live number (or show that it is). */}
      <div className="mt-auto flex items-center justify-between border-t border-border px-5 py-3">
        {inUse ? (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-success-soft px-2.5 py-1 text-xs font-semibold text-success">
            <Power size={11} /> Selected number
          </span>
        ) : (
          <Button size="sm" variant="primary" loading={busy} onClick={onUse} iconLeft={<Power size={12} />}>
            Use this number
          </Button>
        )}

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-2xs text-text-muted">
            <MessageSquare size={11} /> {activity?.templates.total ?? 0} templates
          </span>
          <button onClick={onReconnect} disabled={busy} title="Reconnect this number" aria-label="Reconnect this number" className="rounded-lg p-1.5 text-text-muted hover:bg-surface-alt hover:text-primary"><RefreshCw size={14} /></button>
          <button
            onClick={fixWebhook}
            disabled={busy || fixingWebhook}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-50"
            title="Check & repair the Meta webhook subscription — run this if inbound messages or delivery receipts stop arriving"
          >
            <Webhook size={13} className={fixingWebhook ? 'animate-pulse' : ''} />
          </button>
          <button
            onClick={onDelete}
            disabled={busy}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
            title="Remove account"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    </Card>
  );
}

function SmallNum({ value, label, tone }: { value: number; label: string; tone: string }) {
  return (
    <div>
      <div className={clsx('font-display text-sm font-bold tabular-nums', tone)}>{value.toLocaleString()}</div>
      <div className="text-[9px] uppercase tracking-wide text-text-muted">{label}</div>
    </div>
  );
}
